import type { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import type { RouterOutputs } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { CompactTable, EmptyTableBody, SectionTitle, Stat, Stats } from 'shared/components/section'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table'
import { useDataTable } from 'shared/hooks/use-data-table'
import { type TranslationKey, useTranslate } from 'shared/lib/i18n'
import { cn } from 'shared/lib/utils'
import { WHOLE, formatSum } from './remainders-format'
import { LabelWithHint } from './remainders-parts'

export type NetworkRemaindersData = RouterOutputs['remainders']['Network']
type PointRow = NetworkRemaindersData['points'][number]
type Parcels = NonNullable<NetworkRemaindersData['total']['parcels']>

/**
 * The network's remainders (ticket 47) — as `remainders.Network` answered them;
 * nothing here counts. The Points' total as figures, a row per Point on TezGo
 * (clicking one opens its own remainders, `onOpenPoint`), then each hub — where its
 * Parcels lie now, not added to the total — and the network's money: the Points'
 * Cashboxes, the drivers, the central cashbox, the Registers — only when the answer
 * carries it (support sees none).
 */
export function NetworkRemainders({
	data,
	onOpenPoint,
}: {
	data: NetworkRemaindersData
	onOpenPoint: (point: { pointId: string; id: string }) => void
}) {
	const { t } = useTranslate()
	const total = data.total.parcels
	const seesMoney = !!data.money

	return (
		<div className='flex flex-col gap-8' data-testid='remainders-network'>
			<section className='flex flex-col gap-2'>
				<SectionTitle title={t('remainders.points')} count={data.points.length} />
				{total ? (
					<Stats testId='remainders-total'>
						<Stat label={t('remainders.shelf')} value={total.shelf.total} valueTestId='remainders-total-shelf' />
						<Stat label={t('remainders.expiringSoon')} value={total.shelf.expiringSoon} tone='warn' />
						<Stat label={t('remainders.awaitingDecision')} value={total.shelf.awaitingDecision} tone='bad' />
						<Stat label={t('remainders.incoming')} value={total.incoming.total} />
						<Stat label={t('remainders.pickup')} value={total.pickup.total} />
						<Stat label={t('remainders.unpaid')} value={total.unpaid.shelf.count} tone='warn' />
						{seesMoney ? (
							<Stat
								label={t('remainders.moneyPoints')}
								value={formatSum(data.total.cash ?? 0)}
								valueTestId='remainders-total-cash'
							/>
						) : null}
					</Stats>
				) : null}
				<PointsTable points={data.points} seesMoney={seesMoney} onOpenPoint={onOpenPoint} />
			</section>

			{(data.hubs ?? []).map((hub) => (
				<section key={hub.hubId} className='flex flex-col gap-2' data-testid={`remainders-hub-${hub.hubId}`}>
					<SectionTitle
						title={
							<>
								{t('remainders.hubTitle')} <span className='font-mono'>{hub.id}</span>
							</>
						}
						hint={t('remainders.hubNote')}
					/>
					<Stats>
						<Stat label={t('remainders.hubAtHub')} value={hub.atHub} valueTestId={`remainders-hub-${hub.hubId}-at-hub`} />
						<Stat label={t('remainders.hubInBags')} value={hub.inBags} />
						<Stat label={t('remainders.bagsOnTheWay')} value={hub.bags} />
						<Stat
							label={
								<LabelWithHint
									label={t('remainders.hubShelf')}
									hint={hub.shelf.reasons
										.map((r) => `${t(`remainders.hubReason_${r.reason}` as TranslationKey)}: ${r.count}`)
										.join(' · ')}
								/>
							}
							value={hub.shelf.total}
							tone='warn'
						/>
						{hub.partnerShelves.map((s) => (
							<Stat
								key={s.partnerId ?? 'none'}
								label={t('remainders.hubPartnerShelf', { partner: s.partnerName })}
								value={s.count}
								testId={`remainders-hub-partner-${s.partnerId ?? 'none'}`}
							/>
						))}
					</Stats>
				</section>
			))}

			{data.money ? <NetworkMoney money={data.money} /> : null}
		</div>
	)
}

function PointsTable({
	points,
	seesMoney,
	onOpenPoint,
}: {
	points: readonly PointRow[]
	seesMoney: boolean
	onOpenPoint: (point: { pointId: string; id: string }) => void
}) {
	const { t } = useTranslate()
	const columns = useMemo<ColumnDef<PointRow>[]>(() => {
		const count = (id: string, header: TranslationKey, pick: (p: Parcels) => number): ColumnDef<PointRow> => ({
			id,
			header: t(header),
			meta: { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' },
			cell: ({ row }) => {
				const n = row.original.parcels ? pick(row.original.parcels) : 0
				return <span className={cn(n === 0 && 'text-muted-foreground')}>{n}</span>
			},
		})
		return [
			{
				id: 'point',
				header: t('remainders.colPoint'),
				cell: ({ row }) => (
					<span
						className='font-mono font-semibold'
						data-testid={`remainders-open-${row.original.pointId}`}
						title={t('remainders.openPoint', { point: row.original.id })}
					>
						{row.original.id}
					</span>
				),
			},
			count('shelf', 'remainders.colShelf', (p) => p.shelf.total),
			count('expiring', 'remainders.colExpiring', (p) => p.shelf.expiringSoon),
			count('awaiting', 'remainders.colAwaiting', (p) => p.shelf.awaitingDecision),
			count('incoming', 'remainders.colIncoming', (p) => p.incoming.total),
			count('hubShelf', 'remainders.colHubShelf', (p) => p.hubShelf.total),
			count('pickup', 'remainders.colPickup', (p) => p.pickup.total),
			count('unpaid', 'remainders.colUnpaid', (p) => p.unpaid.shelf.count),
			...(seesMoney
				? [
						{
							id: 'cash',
							header: t('remainders.colCash'),
							meta: { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' },
							cell: ({ row }) => formatSum(row.original.cash?.cash ?? 0),
						} satisfies ColumnDef<PointRow>,
					]
				: []),
		]
	}, [t, seesMoney])
	const data = points as PointRow[]
	const { table } = useDataTable({ data, columns, rowCount: data.length, getRowId: (p) => p.pointId, ...WHOLE })

	return (
		<div data-testid='remainders-network-table'>
			<DataTable
				table={table}
				withPagination={false}
				maxHeightClassName='max-h-none'
				emptyText={<span data-testid='remainders-network-empty'>{t('remainders.networkEmpty')}</span>}
				row={{
					attributes: (p) => ({ 'data-testid': `remainders-row-${p.pointId}` }),
					onRowClick: (p) => onOpenPoint({ pointId: p.pointId, id: p.id }),
				}}
			/>
		</div>
	)
}

function NetworkMoney({ money }: { money: NonNullable<NetworkRemaindersData['money']> }) {
	const { t } = useTranslate()
	const num = 'text-right tabular-nums'
	return (
		<section className='flex flex-col gap-2' data-testid='remainders-money'>
			<SectionTitle title={t('remainders.money')} />
			<Stats>
				<Stat label={t('remainders.moneyPoints')} value={formatSum(money.pointsCash)} valueTestId='remainders-money-points' />
				<Stat label={t('remainders.moneyDrivers')} value={formatSum(money.driversCash)} valueTestId='remainders-money-drivers' />
				<Stat
					label={t('remainders.moneyCentral')}
					value={formatSum(money.centralCashbox.cash)}
					valueTestId='remainders-money-central'
				/>
			</Stats>
			<CompactTable testId='remainders-money-registers'>
				<TableHeader>
					<TableRow>
						<TableHead>{t('remainders.colPartner')}</TableHead>
						<TableHead className='text-right'>{t('remainders.colToPay')}</TableHead>
						<TableHead className='text-right'>{t('remainders.colPaid')}</TableHead>
						<TableHead className='text-right'>{t('remainders.colNotOnRegister')}</TableHead>
					</TableRow>
				</TableHeader>
				{money.registers.length === 0 ? (
					<EmptyTableBody columns={4}>{t('remainders.registersEmpty')}</EmptyTableBody>
				) : (
					<TableBody>
						{money.registers.map((r) => (
							<TableRow key={r.partnerId} data-testid={`remainders-register-${r.partnerId}`}>
								<TableCell className='font-medium'>{r.partnerName}</TableCell>
								<TableCell className={num}>{formatSum(r.toPay)}</TableCell>
								<TableCell className={num}>{formatSum(r.paid)}</TableCell>
								<TableCell className={num}>{formatSum(r.notOnRegister)}</TableCell>
							</TableRow>
						))}
					</TableBody>
				)}
			</CompactTable>
		</section>
	)
}
